import * as THREE from 'three'
import type { ClipConfig } from './types'

export class ClippingManager {
  private clipPlane: THREE.Plane
  private _enabled = false
  private _position = 0
  private _axis: 'x' | 'y' | 'z' = 'x'
  private capMaterial: THREE.MeshBasicMaterial | null = null
  private capMesh: THREE.Mesh | null = null
  private scene: THREE.Scene
  private renderer: THREE.WebGLRenderer

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer) {
    this.scene = scene
    this.renderer = renderer
    this.clipPlane = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0)
  }

  get enabled() { return this._enabled }
  get position() { return this._position }
  get axis() { return this._axis }
  get plane() { return this.clipPlane }

  apply(config: ClipConfig, modelGroup?: THREE.Group) {
    this._enabled = config.enabled
    this._position = config.position
    this._axis = config.axis

    this.renderer.localClippingEnabled = config.enabled

    const axisVec = new THREE.Vector3(
      config.axis === 'x' ? 1 : 0,
      config.axis === 'y' ? 1 : 0,
      config.axis === 'z' ? 1 : 0,
    )
    this.clipPlane.normal.copy(axisVec)
    this.clipPlane.constant = config.position

    if (modelGroup) {
      this.applyToModel(modelGroup)
    }

    this.updateCap(config)
  }

  private applyToModel(group: THREE.Group) {
    const planes = this._enabled ? [this.clipPlane] : []
    group.traverse(c => {
      if (c instanceof THREE.Mesh && c.material) {
        const mats = Array.isArray(c.material) ? c.material : [c.material]
        for (const m of mats) {
          m.clippingPlanes = planes
          m.clipShadows = true
        }
      }
    })
  }

  private updateCap(config: ClipConfig) {
    this.removeCap()
    if (!config.enabled || !config.showCap) return

    this.capMaterial = new THREE.MeshBasicMaterial({
      color: 0x00aaff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
    })

    const geo = new THREE.PlaneGeometry(10, 10)
    this.capMesh = new THREE.Mesh(geo, this.capMaterial)

    const axisVec = new THREE.Vector3(
      config.axis === 'x' ? 1 : 0,
      config.axis === 'y' ? 1 : 0,
      config.axis === 'z' ? 1 : 0,
    )
    this.capMesh.position.copy(axisVec.clone().multiplyScalar(-config.position))

    if (config.axis === 'x') {
      this.capMesh.rotation.y = Math.PI / 2
    } else if (config.axis === 'z') {
      this.capMesh.rotation.x = Math.PI / 2
    }

    this.scene.add(this.capMesh)
  }

  private removeCap() {
    if (this.capMesh) {
      this.scene.remove(this.capMesh)
      this.capMesh.geometry.dispose()
      this.capMesh = null
    }
    if (this.capMaterial) {
      this.capMaterial.dispose()
      this.capMaterial = null
    }
  }

  dispose() {
    this.removeCap()
    this.renderer.localClippingEnabled = false
  }
}
